import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatCurrency } from '@/lib/format'
import { PayablesData } from '@/api/types'
import { dashboardsService } from '@/services/dashboardsService'

interface PayablesChartProps {
  branchId: number
  className?: string
}

/**
 * Payables chart component
 * Shows a horizontal stacked bar chart with Current and Overdue payables
 */
export const PayablesChart: React.FC<PayablesChartProps> = ({
  branchId,
  className,
}) => {
  const [data, setData] = useState<PayablesData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPayables = async () => {
      if (!branchId) return

      try {
        setLoading(true)
        const payablesData = await dashboardsService.getPayables(branchId)
        setData(payablesData)
      } catch (error) {
        console.error('Error loading payables data:', error)
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchPayables()
  }, [branchId])

  // Validate and normalize data - round all values to integers
  const normalizedData = React.useMemo(() => {
    if (!data) return null

    const total = Math.round(Number(data.totalPayables) || 0)
    const current = Math.round(Number(data.currentPayables) || 0)
    const overdue = Math.round(Number(data.overduePayables) || 0)

    // Ensure all values are finite numbers
    if (!isFinite(total) || !isFinite(current) || !isFinite(overdue)) {
      return null
    }

    return {
      totalPayables: total,
      currentPayables: current,
      overduePayables: overdue,
    }
  }, [data])


  // Calculate percentages
  const currentPercentage = normalizedData && normalizedData.totalPayables > 0
    ? (normalizedData.currentPayables / normalizedData.totalPayables) * 100
    : 0

  const overduePercentage = normalizedData && normalizedData.totalPayables > 0
    ? (normalizedData.overduePayables / normalizedData.totalPayables) * 100
    : 0


  // Format currency with k/M suffix for axis labels
  const formatAxisLabel = (value: number) => {
    if (!isFinite(value) || isNaN(value) || value < 0) {
      return 'SAR 0'
    }
    
    if (value >= 1000000) {
      return `SAR ${(value / 1000000).toFixed(0)}M`
    } else if (value >= 1000) {
      return `SAR ${(value / 1000).toFixed(0)}k`
    }
    return `SAR ${value.toFixed(0)}`
  }

  // Calculate bar widths as percentages
  const currentWidthPercent = normalizedData && normalizedData.totalPayables > 0
    ? (normalizedData.currentPayables / normalizedData.totalPayables) * 100
    : 0

  const overdueWidthPercent = normalizedData && normalizedData.totalPayables > 0
    ? (normalizedData.overduePayables / normalizedData.totalPayables) * 100
    : 0

  // Generate axis ticks
  interface AxisTick {
    value: number
    label: string
    position: number
  }

  const axisTicks = React.useMemo((): AxisTick[] => {
    if (!normalizedData || normalizedData.totalPayables <= 0) {
      return [
        { value: 0, label: formatAxisLabel(0), position: 0 },
        { value: 25, label: formatAxisLabel(25), position: 25 },
        { value: 50, label: formatAxisLabel(50), position: 50 },
        { value: 75, label: formatAxisLabel(75), position: 75 },
        { value: 100, label: formatAxisLabel(100), position: 100 },
      ]
    }
    const total = normalizedData.totalPayables
    return [
      { value: 0, label: formatAxisLabel(0), position: 0 },
      { value: total * 0.25, label: formatAxisLabel(total * 0.25), position: 25 },
      { value: total * 0.5, label: formatAxisLabel(total * 0.5), position: 50 },
      { value: total * 0.75, label: formatAxisLabel(total * 0.75), position: 75 },
      { value: total, label: formatAxisLabel(total), position: 100 },
    ]
  }, [normalizedData])

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Total Payables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!normalizedData || normalizedData.totalPayables <= 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Total Payables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">No data available</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          Total Payables: {formatCurrency(normalizedData.totalPayables)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Custom Horizontal Stacked Bar Chart */}
          <div className="w-full">
            {/* Chart Bar */}
            <div className="relative w-full h-12 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden">
              {/* Current Payables Bar */}
              {currentWidthPercent > 0 && (
                <div
                  className="absolute left-0 top-0 h-full bg-[#0f172a] transition-all duration-500"
                  style={{ width: `${currentWidthPercent}%` }}
                  title={`Current: ${formatCurrency(normalizedData.currentPayables)}`}
                />
              )}
              {/* Overdue Payables Bar */}
              {overdueWidthPercent > 0 && (
                <div
                  className="absolute h-full bg-[#64748b] transition-all duration-500 rounded-r-md"
                  style={{
                    left: `${currentWidthPercent}%`,
                    width: `${overdueWidthPercent}%`,
                  }}
                  title={`Overdue: ${formatCurrency(normalizedData.overduePayables)}`}
                />
              )}
            </div>

            {/* X-Axis Labels */}
            <div className="relative w-full mt-2 h-8">
              {axisTicks.map((tick, index) => (
                <div
                  key={index}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: tick.position === 0 ? '0%' : tick.position === 100 ? '100%' : `${tick.position}%`,
                    transform: tick.position === 0 ? 'translateX(0)' : tick.position === 100 ? 'translateX(-100%)' : 'translateX(-50%)',
                  }}
                >
                  <div className="w-px h-2 bg-gray-400 dark:bg-gray-600" />
                  <span className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                    {tick.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 pt-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-[#0f172a]" />
              <span className="text-sm text-muted-foreground">
                Current: {formatCurrency(normalizedData.currentPayables)} ({currentPercentage.toFixed(0)}%)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-[#64748b]" />
              <span className="text-sm text-muted-foreground">
                Overdue: {formatCurrency(normalizedData.overduePayables)} ({overduePercentage.toFixed(0)}%)
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

